import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, EmptyState, LoadingState } from "../components/states";
import {
  GameStatusCard,
  SafeTestPanel,
  ValidationFindings,
  WorkshopMonitor,
  NO_AUTO_RESTART_NOTE,
} from "../components/integrations";
import {
  api,
  type GameStatusView,
  type IntegrationConfigValidation,
  type SafeIntegration,
  type SafeTestResult,
  type WorkshopUpdateView,
} from "../lib/api";

interface GameDraft {
  serverDisplayName: string;
  adapter: "http-json" | "tcp-health" | "webhook-ingest";
  endpointRef: string;
  pollIntervalSeconds: number;
  timeoutMs: number;
  targetGuildId: string;
  targetChannelKey: string;
  statusMessageMode: "none" | "notify" | "persistent";
  onlineThreshold: number;
  offlineThreshold: number;
}

function readGame(config: unknown): GameDraft {
  const c = (config ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : "");
  const num = (k: string, d: number) => (typeof c[k] === "number" ? (c[k] as number) : d);
  return {
    serverDisplayName: str("serverDisplayName"),
    adapter: (["http-json", "tcp-health", "webhook-ingest"] as const).includes(c["adapter"] as never) ? (c["adapter"] as GameDraft["adapter"]) : "http-json",
    endpointRef: str("endpointRef"),
    pollIntervalSeconds: num("pollIntervalSeconds", 300),
    timeoutMs: num("timeoutMs", 5000),
    targetGuildId: str("targetGuildId"),
    targetChannelKey: str("targetChannelKey"),
    statusMessageMode: (["none", "notify", "persistent"] as const).includes(c["statusMessageMode"] as never) ? (c["statusMessageMode"] as GameDraft["statusMessageMode"]) : "notify",
    onlineThreshold: num("onlineThreshold", 1),
    offlineThreshold: num("offlineThreshold", 2),
  };
}

/**
 * Game-server setup (section 30). Endpoint validation + status preview + polling
 * policy + persistent-status option + alert thresholds.
 *
 * SAFETY: this surface has NO control that restarts a server or applies any
 * update. Monitoring and safe validation only.
 */
export function GameServerSetup({
  integration,
  status,
  validation,
  testResult,
  busy,
  message,
  onSaveValidate,
  onLoadStatus,
  onTest,
}: {
  integration: SafeIntegration;
  status: GameStatusView | null | undefined;
  validation: IntegrationConfigValidation | null;
  testResult: SafeTestResult | null;
  busy?: boolean;
  message?: string | null;
  onSaveValidate: (config: Record<string, unknown>) => void;
  onLoadStatus: () => void;
  onTest: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<GameDraft>(() => readGame(integration.configuration));
  const set = <K extends keyof GameDraft>(k: K, v: GameDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = () => onSaveValidate({ ...draft, targetChannelKey: draft.targetChannelKey || null, enabled: false });

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>Monitoring only.</strong>
        <span>{NO_AUTO_RESTART_NOTE}</span>
      </div>

      <div className="world-panel world-panel--proposed">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Server configuration
        </h4>
        <div className="card-grid">
          <label className="field">
            <span className="field__label">Server display name</span>
            <input className="field__input" value={draft.serverDisplayName} onChange={(e) => set("serverDisplayName", e.target.value)} aria-label="Server display name" />
          </label>
          <label className="field">
            <span className="field__label">Adapter</span>
            <select className="field__input" value={draft.adapter} onChange={(e) => set("adapter", e.target.value as GameDraft["adapter"])} aria-label="Server adapter">
              <option value="http-json">http-json</option>
              <option value="tcp-health">tcp-health</option>
              <option value="webhook-ingest">webhook-ingest</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Endpoint reference</span>
            <input className="field__input" value={draft.endpointRef} onChange={(e) => set("endpointRef", e.target.value)} aria-label="Server endpoint reference" />
          </label>
          <label className="field">
            <span className="field__label">Poll interval (seconds)</span>
            <input className="field__input" type="number" value={draft.pollIntervalSeconds} onChange={(e) => set("pollIntervalSeconds", Number(e.target.value))} aria-label="Server poll interval seconds" />
          </label>
          <label className="field">
            <span className="field__label">Timeout (ms)</span>
            <input className="field__input" type="number" value={draft.timeoutMs} onChange={(e) => set("timeoutMs", Number(e.target.value))} aria-label="Server timeout ms" />
          </label>
          <label className="field">
            <span className="field__label">Target guild id</span>
            <input className="field__input" value={draft.targetGuildId} onChange={(e) => set("targetGuildId", e.target.value)} aria-label="Server target guild id" />
          </label>
          <label className="field">
            <span className="field__label">Target channel key (optional)</span>
            <input className="field__input" value={draft.targetChannelKey} onChange={(e) => set("targetChannelKey", e.target.value)} aria-label="Server target channel key" />
          </label>
          <label className="field">
            <span className="field__label">Status message mode</span>
            <select className="field__input" value={draft.statusMessageMode} onChange={(e) => set("statusMessageMode", e.target.value as GameDraft["statusMessageMode"])} aria-label="Status message mode">
              <option value="none">none</option>
              <option value="notify">notify</option>
              <option value="persistent">persistent status message</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Online threshold</span>
            <input className="field__input" type="number" value={draft.onlineThreshold} onChange={(e) => set("onlineThreshold", Number(e.target.value))} aria-label="Online alert threshold" />
          </label>
          <label className="field">
            <span className="field__label">Offline threshold</span>
            <input className="field__input" type="number" value={draft.offlineThreshold} onChange={(e) => set("offlineThreshold", Number(e.target.value))} aria-label="Offline alert threshold" />
          </label>
        </div>
        <div className="row" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
          <button type="button" className="btn btn--primary" disabled={busy || draft.serverDisplayName.length === 0} onClick={save}>
            {busy ? "Working…" : "Save & validate endpoint"}
          </button>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={onLoadStatus}>
            Load status preview
          </button>
        </div>
        {validation ? <ValidationFindings validation={validation} /> : null}
        {message ? <p className="muted" role="status">{message}</p> : null}
      </div>

      <div className="world-panel world-panel--current">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Status preview
        </h4>
        {status === undefined ? <p className="muted">Load a status preview to see the latest known server state.</p> : <GameStatusCard status={status} />}
      </div>

      <SafeTestPanel result={testResult} onRunTest={onTest} busy={busy} />
    </div>
  );
}

export function IntegrationGameWorkshop(): JSX.Element {
  return (
    <IntegrationPage title="Game server & Workshop monitoring" intro={<p>Monitor a game server and Steam Workshop items. ODIN never restarts a server or applies an update.</p>}>
      {(tenantId) => <GameWorkshopPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

const GAME_TYPES = new Set(["GAME_SERVER"]);

function GameWorkshopPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [params] = useSearchParams();
  const [list, setList] = useState<SafeIntegration[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(params.get("id"));
  const [integration, setIntegration] = useState<SafeIntegration | null>(null);
  const [status, setStatus] = useState<GameStatusView | null | undefined>(undefined);
  const [updates, setUpdates] = useState<WorkshopUpdateView[] | null>(null);
  const [validation, setValidation] = useState<IntegrationConfigValidation | null>(null);
  const [testResult, setTestResult] = useState<SafeTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.integrations
      .list(tenantId)
      .then((r) => {
        const relevant = r.integrations.filter((i) => GAME_TYPES.has(i.type) || i.type === "STEAM_WORKSHOP");
        setList(relevant);
        if (!selectedId && relevant[0]) setSelectedId(relevant[0].id);
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId]);

  useEffect(() => {
    if (!selectedId) return;
    api.integrations
      .get(tenantId, selectedId)
      .then((r) => {
        setIntegration(r.integration);
        setValidation(null);
        setStatus(undefined);
        setUpdates(null);
        setTestResult(null);
        if (r.integration.type === "STEAM_WORKSHOP") {
          api.integrations.getWorkshop(tenantId, r.integration.id).then((w) => setUpdates(w.updates)).catch(() => setUpdates([]));
        }
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId, selectedId]);

  const saveValidate = async (config: Record<string, unknown>) => {
    if (!integration) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.integrations.updateConfiguration(tenantId, integration.id, config);
      const r = await api.integrations.validate(tenantId, integration.id);
      setValidation(r.validation);
      setIntegration(r.integration);
      setMessage(r.validation.valid ? "Endpoint configuration validated." : `Validation found ${r.validation.errors.length} issue(s).`);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const loadStatus = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.getGame(tenantId, integration.id);
      setStatus(r.status);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.test(tenantId, integration.id);
      setTestResult(r);
      setMessage(r.note);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ApiUnavailableState detail={error} />;
  if (list === null) return <LoadingState label="Reading game / workshop integrations…" />;

  const isWorkshop = integration?.type === "STEAM_WORKSHOP";

  return (
    <div className="ob-builder">
      <label className="field">
        <span className="field__label">Game / Workshop integration</span>
        <select className="field__input" value={selectedId ?? ""} onChange={(e) => setSelectedId(e.target.value || null)} aria-label="Select game or workshop integration">
          <option value="">Select an integration…</option>
          {list.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.type}, {i.status})
            </option>
          ))}
        </select>
      </label>

      {!integration ? (
        <EmptyState title="No integration selected">Create a game-server or Steam Workshop integration on the Integrations page first.</EmptyState>
      ) : isWorkshop ? (
        <div className="ob-builder">
          <h4 className="section-title">Workshop item monitoring</h4>
          {updates === null ? <LoadingState label="Reading workshop updates…" /> : <WorkshopMonitor updates={updates} />}
          <SafeTestPanel result={testResult} onRunTest={doTest} busy={busy} />
          {message ? <p className="muted" role="status">{message}</p> : null}
        </div>
      ) : (
        <GameServerSetup
          integration={integration}
          status={status}
          validation={validation}
          testResult={testResult}
          busy={busy}
          message={message}
          onSaveValidate={saveValidate}
          onLoadStatus={loadStatus}
          onTest={doTest}
        />
      )}
    </div>
  );
}
